import { useAppSelector } from '@/hooks/useAppSelector';
import { selectTrialStatus, selectIsTrial } from '@/features/subscriptions/subscriptionsSlice';
import { Clock, AlertTriangle } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';

/**
 * Trial Banner Component
 * Displays a banner at the top of the app showing trial status and days remaining.
 * Only shows when user is on a trial subscription.
 */
export function TrialBanner() {
  const navigate = useNavigate();
  const isTrial = useAppSelector(selectIsTrial);
  const trialStatus = useAppSelector(selectTrialStatus);

  // Don't render if not on trial
  if (!isTrial || !trialStatus) {
    return null;
  }

  const { days_remaining, is_expired, message } = trialStatus;

  // Determine banner style based on urgency. Text/icon colour is picked per
  // tier, not assumed to be white: bg-warning-solid (ochre) fails AA with
  // white text (design system spec §7.6), and bg-brand is tenant-customisable
  // so it needs the brand's own on-brand text token rather than a hardcoded
  // color.
  let bannerClass = 'bg-brand'; // Default: informational
  let textClass = 'text-brand-fg';
  let Icon = Clock;

  if (is_expired) {
    bannerClass = 'bg-danger-solid';
    textClass = 'text-white';
    Icon = AlertTriangle;
  } else if (days_remaining <= 3) {
    bannerClass = 'bg-warning-solid';
    textClass = 'text-ink';
    Icon = AlertTriangle;
  }

  return (
    <div className={`${bannerClass} ${textClass} px-4 py-2`}>
      <div className="container mx-auto flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Icon className="h-4 w-4" />
          <span className="text-sm font-medium">{message}</span>
        </div>
        <Button
          variant="secondary"
          size="sm"
          onClick={() => navigate('/settings/subscription')}
          className="bg-surface text-ink hover:bg-surface-2"
        >
          {is_expired ? 'Select a plan' : 'View plans'}
        </Button>
      </div>
    </div>
  );
}

export default TrialBanner;
